import type { Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Formula } from "building-types/options/formula";
import type { RWTable } from "common/data/table";

export type DisconnectedReason = "ENTITY_DISABLED" | "IS_REDUNDANT" | "FORCE_LOCK";

export interface EntityParent {
  name: string;
  expr: Formula | null;
  handle: DocEntityHandle;
  parent: EntityParent | null;
}

export interface EntityChild {
  name: string;
  expr: Formula | null; // references option set that might be redundant
  handle: DocEntityHandle;
  optionSetIsRedundant: boolean; // is option set redundant in predicate expr ?
  cannotEvalExpr: boolean; // is evaluating expr throwing an exception ?
}

export interface EntityHierarchy {
  redundantChildren?: EntityChildren;
  effectiveChildren?: EntityChildren;
  children: EntityChildren;
  parent: EntityParent | null;
}

export type OptionSetIdToLevelId = RWTable<string | undefined | null>; // keyed by optionSetId

export type EntityChildren = RWTable<EntityChild>; // key is child handle hash

export type EntityHierarchies = RWTable<EntityHierarchy>; // key is parent handle hash

export type OptionSetIdToEntityHierarchies = RWTable<EntityHierarchies>; // keyed by optionSetId

export interface OptionSetEntityRelationship {
  /**
   * Hierarchies of entities where child nodes represent entities with a disabled condition predicate
   * that happens to reference optionSetId (in this interface below) in the predicate.
   */
  hierarchies: EntityHierarchies;

  /**
   * Helper/substitute for accessing Doc.optionSets without need to pass around a Doc or Doc.optionSets
   */
  allOptionSets: Doc["optionSets"];

  /**
   * Option set ids (symbols) found in a DocOptionLocks.disconnectCondition predicate.
   */
  disconnectIdentifiers?: string[];

  /**
   * Used to index into Doc.optionSets and Doc.optionLocks to obtain a DocOptionSet and a
   * DocOptionLock respectively. It identifies the option set that is linked to 1 or more
   * entities in hierarchies (see above).
   */
  optionSetId: string;

  /**
   * Whether or not the option set is sourced from an external Document Reference
   */
  isEntityRef?: boolean;

  /**
   * The reasons this option set is disconnected (locked). Only set for disconnected option sets.
   * Multiple reasons can apply simultaneously (e.g., both FORCE_LOCK and ENTITY_DISABLED).
   */
  disconnectedReasons?: DisconnectedReason[];
}

/**
 * Serialized version of OptionSetEntityRelationship for worker thread communication.
 * Replaces allOptionSets with optionSetNames to avoid massive data duplication.
 */
export interface SerializedOptionSetEntityRelationship {
  /**
   * Hierarchies of entities (same as OptionSetEntityRelationship).
   */
  hierarchies: EntityHierarchies;

  /**
   * Pre-computed option set names, replacing allOptionSets.
   * Only the names are needed for tooltip display.
   */
  optionSetNames: { [id: string]: string };

  /**
   * Option set ids found in disconnectCondition predicate.
   */
  disconnectIdentifiers?: string[];

  /**
   * The option set id this relationship is for.
   */
  optionSetId: string;

  /**
   * Whether the option set is sourced from an external Document Reference.
   */
  isEntityRef?: boolean;

  /**
   * The reasons this option set is disconnected.
   */
  disconnectedReasons?: DisconnectedReason[];
}

/**
 * Serialized version of OptionSetState for worker thread communication.
 * The optionSet is stored separately in evaluatedOptionSets to avoid duplication.
 */
export interface SerializedOptionSetState {
  id: string;
  currentValue: string;
  disconnectedOptions?: { [id: string]: boolean };
  disconnectConditionOptions?: { [id: string]: Formula };
}

/**
 * A "disconnected" option set, also known as a "locked option", is an option set that:
 *
 * 1. Has no effect, because every child that references it in a child condition predicate is missing because its
 *    immediate parent is missing, OR
 * 2. Is redundant in all child condition predicates that reference it, because the condition predicate always evaluates
 *    to the same boolean result, no matter what option is chosen in that option set OR
 * 3. Has the `disconnectCondition` field set in a corresponding DocOptionLock so that, when evaluated, it causes a
 *    forced disconnection to occur even when the two cases above are not true.
 */

export type OptionSetEntityRelationshipTable = RWTable<OptionSetEntityRelationship>;
export type OptionSetEntityDisconnectedTable = OptionSetEntityRelationshipTable;
// Conversely, "connected" option sets are known as "unlocked options"

export type OptionSetEntityConnectedTable = OptionSetEntityRelationshipTable;

export type SerializedOptionSetEntityRelationshipTable = RWTable<SerializedOptionSetEntityRelationship>;
export type SerializedOptionSetEntityDisconnectedTable = SerializedOptionSetEntityRelationshipTable;
export type SerializedOptionSetEntityConnectedTable = SerializedOptionSetEntityRelationshipTable;

export interface OptionSetEntityRelations {
  optionSetIdToEntityHierarchies: OptionSetIdToEntityHierarchies;
  optionSetIdToAffectedEntities: RWTable<DocEntityHandle[]>;
  optionSetIdToLevelId: OptionSetIdToLevelId;
}

export function optionSetEntityRelationsDefault(): OptionSetEntityRelations {
  return {
    optionSetIdToEntityHierarchies: {},
    optionSetIdToAffectedEntities: {},
    optionSetIdToLevelId: {},
  };
}

export const UNKNOWN_OPTION_SET_NAME = "An unknown option";
